import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { ApiContext } from '../../contexts/ApiContext';
import { useNotification } from '../../contexts/NotificationContext';
import PrintService from '../../service/print.service';

function formatLastSeen(value) {
  if (!value) return 'Nunca';
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch {
    return String(value);
  }
}

const fieldBtnStyle = {
  flexShrink: 0,
  whiteSpace: 'nowrap',
  minWidth: 88,
};

function PrintingSettingsPage() {
  const { account } = useContext(ApiContext);
  const { notify } = useNotification();
  const accountId = account?.id;
  const enabled = !!account?.printingEnabled;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [kitchenPrinterId, setKitchenPrinterId] = useState('');
  const [cashierPrinterId, setCashierPrinterId] = useState('');
  const [printAutoOnConfirm, setPrintAutoOnConfirm] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const load = useCallback(async () => {
    if (!accountId || !enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [statusData, creds] = await Promise.all([
        PrintService.getStatus(accountId),
        PrintService.getCredentials(accountId),
      ]);
      setStatus(statusData);
      setCredentials(creds);
      setPrintAutoOnConfirm(!!statusData.printAutoOnConfirm);
      const kitchen = statusData.printers?.find((p) => p.isDefaultKitchen);
      const cashier = statusData.printers?.find((p) => p.isDefaultCashier);
      setKitchenPrinterId(kitchen ? String(kitchen.id) : '');
      setCashierPrinterId(cashier ? String(cashier.id) : '');
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao carregar status de impressão', 'icon-alert-circle-exc');
    } finally {
      setLoading(false);
    }
  }, [accountId, enabled, notify]);

  useEffect(() => {
    load();
    if (!accountId || !enabled) return undefined;
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load, accountId, enabled]);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(String(text));
      notify('tr', 'success', `${label} copiado`, 'icon-check-2');
    } catch {
      notify('tr', 'danger', 'Não foi possível copiar', 'icon-alert-circle-exc');
    }
  };

  const handleSave = async () => {
    if (!accountId) return;
    setSaving(true);
    try {
      const updated = await PrintService.updateConfig(accountId, {
        printAutoOnConfirm,
        kitchenPrinterId: kitchenPrinterId ? Number(kitchenPrinterId) : null,
        cashierPrinterId: cashierPrinterId ? Number(cashierPrinterId) : null,
      });
      setStatus(updated);
      notify('tr', 'success', 'Configurações salvas', 'icon-check-2');
    } catch (err) {
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao salvar',
        'icon-alert-circle-exc',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!enabled) {
    return (
      <div className="content">
        <Card>
          <CardBody>
            <p className="mb-0 text-muted">
              O módulo de impressão não está ativo nesta conta. Solicite a liberação ao suporte.
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (loading && !status) {
    return (
      <div className="content d-flex justify-content-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  const online = !!status?.online;
  const printers = status?.printers || [];

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card className="mb-3">
            <CardBody className="py-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
                <div>
                  <strong>PedeGo Impressões</strong>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    {online ? 'Conectado' : 'Desconectado'}
                    {status?.lastSeenAt ? ` · Último sinal: ${formatLastSeen(status.lastSeenAt)}` : ''}
                    {status?.platform ? ` · ${status.platform}` : ''}
                  </div>
                </div>
                <span
                  className={`badge badge-${online ? 'success' : 'secondary'}`}
                  style={{ fontSize: 12, fontWeight: 600, padding: '6px 10px' }}
                >
                  {online ? 'Online' : 'Offline'}
                </span>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg="6" className="mb-3">
          <Card className="h-100">
            <CardHeader>
              <CardTitle tag="h4">Credenciais do agente</CardTitle>
              <p className="card-category mb-0">
                Use estes dados no app instalado no PC da loja.
              </p>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>ID da conta</Label>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <Input
                    className="flex-grow-1"
                    value={credentials?.accountId ?? accountId ?? ''}
                    readOnly
                  />
                  <Button
                    color="default"
                    outline
                    type="button"
                    style={fieldBtnStyle}
                    onClick={() => copyText(credentials?.accountId ?? accountId, 'ID')}
                  >
                    Copiar
                  </Button>
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Token da conta</Label>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <Input
                    className="flex-grow-1"
                    type={showToken ? 'text' : 'password'}
                    value={credentials?.token || ''}
                    readOnly
                  />
                  <Button
                    color="default"
                    outline
                    type="button"
                    style={fieldBtnStyle}
                    onClick={() => setShowToken((v) => !v)}
                  >
                    {showToken ? 'Ocultar' : 'Mostrar'}
                  </Button>
                  <Button
                    color="default"
                    outline
                    type="button"
                    style={fieldBtnStyle}
                    onClick={() => copyText(credentials?.token, 'Token')}
                  >
                    Copiar
                  </Button>
                </div>
                <small className="text-muted d-block mt-2">
                  Não compartilhe o token. Quem tiver acesso pode receber jobs de impressão desta conta.
                </small>
              </FormGroup>

              <div
                className="mt-3 p-3"
                style={{
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.03)',
                }}
              >
                <p className="mb-1" style={{ fontSize: 13 }}>
                  Baixe o <strong>PedeGo Impressões</strong>, use a API{' '}
                  <code>https://api.pedego.online</code>, o ID e o token.
                </p>
                <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                  O programa precisa ficar aberto (ou na bandeja) para imprimir.
                </p>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg="6" className="mb-3">
          <Card className="h-100">
            <CardHeader>
              <CardTitle tag="h4">Impressoras e regras</CardTitle>
              <p className="card-category mb-0">
                Aparecem após o agente conectar e sincronizar.
              </p>
            </CardHeader>
            <CardBody>
              {printers.length === 0 ? (
                <p className="text-muted mb-4" style={{ fontSize: 14 }}>
                  Nenhuma impressora sincronizada. Abra o PedeGo Impressões no computador da loja.
                </p>
              ) : (
                <>
                  <FormGroup>
                    <Label>Impressora da cozinha</Label>
                    <Input
                      type="select"
                      value={kitchenPrinterId}
                      onChange={(e) => setKitchenPrinterId(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {printers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                  <FormGroup>
                    <Label>Impressora do caixa</Label>
                    <Input
                      type="select"
                      value={cashierPrinterId}
                      onChange={(e) => setCashierPrinterId(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {printers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </>
              )}

              <FormGroup check className="mb-4">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={printAutoOnConfirm}
                    onChange={(e) => setPrintAutoOnConfirm(e.target.checked)}
                  />{' '}
                  Imprimir automaticamente ao confirmar pedido
                </Label>
              </FormGroup>

              <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                <Button color="primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar configurações'}
                </Button>
                <Button color="default" outline onClick={load} disabled={loading}>
                  Atualizar
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default PrintingSettingsPage;
